/**
 * Centralized Menu Configuration
 * 
 * Single source of truth for all menu sections, items, and permissions.
 * Used by both UnifiedMenu (navigation) and RoleManagement (permission UI).
 */

// Menu item definition
export interface MenuItemConfig {
  id: string;
  label: string;
  path?: string;
  badge?: string;
  adminOnly?: boolean;
  permission?: string;  // Page-level permission (path used if not specified)
}

// Menu section definition
export interface MenuSectionConfig {
  id: string;
  label: string;
  description: string;
  requiredPermission: string;
  items: MenuItemConfig[];
  gradient?: string;
  accentColor?: string;
  /** When true the section is omitted from the sidebar but the URL remains accessible. */
  hidden?: boolean;
}

// Feature permission definition
export interface FeatureConfig {
  key: string;
  label: string;
  description: string;
  defaultValue: boolean;
}

// All menu sections - THE SINGLE SOURCE OF TRUTH
export const MENU_SECTIONS: MenuSectionConfig[] = [
  {
    id: 'overview',
    label: 'Home',
    description: 'Dashboard and home page',
    requiredPermission: 'overview',
    gradient: 'linear-gradient(135deg, #0d5c2e 0%, #148a44 100%)',
    accentColor: '#0d5c2e',
    items: [
      { id: 'home', label: 'Home', path: '/' },
    ],
  },
  {
    id: 'data_management',
    label: 'Data Management',
    description: 'Sources, harmonization, config',
    requiredPermission: 'data_management',
    gradient: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)',
    accentColor: '#2563eb',
    items: [
      { id: 'sources', label: 'Data Sources', path: '/sources' },
      { id: 'harmonization', label: 'Org Unit Harmonization', path: '/harmonization' },
      { id: 'dictionary', label: 'Data Dictionary', path: '/ai/dictionary' },
    ],
  },
  {
    id: 'pipeline',
    label: 'Pipeline',
    description: 'Pipeline control and history',
    requiredPermission: 'pipeline',
    gradient: 'linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)',
    accentColor: '#7c3aed',
    items: [
      { id: 'pipelines', label: 'Pipelines', path: '/dashboards/pipeline' },
      { id: 'extraction', label: 'Extraction Monitor', path: '/dashboards/monitoring' },
      { id: 'schedule', label: 'Schedule Manager', path: '/dashboards/schedule' },
      { id: 'quality', label: 'Data Quality', path: '/quality' },
    ],
  },
  {
    id: 'infrastructure',
    label: 'Infrastructure',
    description: 'Services, terminal, file browser',
    requiredPermission: 'infrastructure',
    gradient: 'linear-gradient(135deg, #0ea5e9 0%, #38bdf8 100%)',
    accentColor: '#0ea5e9',
    items: [
      { id: 'services', label: 'Services', path: '/dashboards/services' },
      { id: 'terminal', label: 'Web Terminal', path: '/infrastructure/terminal' },
      { id: 'files', label: 'File Browser', path: '/infrastructure/files' },
      { id: 'events', label: 'Service Events', path: '/infrastructure/events' },
      { id: 'images', label: 'Image Management', path: '/infrastructure/images' },
    ],
  },
  {
    id: 'monitoring',
    label: 'Monitoring',
    description: 'System health and extraction monitoring',
    requiredPermission: 'monitoring',
    gradient: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
    accentColor: '#059669',
    items: [
      { id: 'pools', label: 'Airflow Monitor', path: '/dashboards/pools' },
      { id: 'system', label: 'System Health', path: '/dashboards/system-health' },
      { id: 'pulse', label: 'System Load Monitor', path: '/dashboards/pulse' },
      { id: 'redis', label: 'Caching', path: '/dashboards/redis' },
      { id: 'errors', label: 'System Errors', path: '/dashboards/errors' },
      { id: 'logs', label: 'Logs', path: '/dashboards/logs' },
    ],
  },
  /* ── API PLATFORM ── */
  {
    id: 'api_platform',
    label: 'API Platform',
    description: 'API gateway, access monitoring, federation, consumer & product management',
    requiredPermission: 'administration',
    gradient: 'linear-gradient(135deg, #0f766e 0%, #14b8a6 100%)',
    accentColor: '#0f766e',
    items: [
      { id: 'gateway', label: 'API Gateway', path: '/dashboards/gateway' },
      { id: 'marketplace', label: 'API Management', path: '/dashboards/marketplace' },
      { id: 'developer', label: 'Developer Portal', path: '/dashboards/developer-portal' },
      { id: 'custom-endpoints', label: 'API Endpoints', path: '/dashboards/custom-endpoints' },
      { id: 'api-docs', label: 'API Reference', path: '/docs/api-reference' },
    ],
  },
  /* ── SURVEILLANCE — §9.7 Revised Sidebar ── */
  {
    id: 'surveillance',
    label: 'Surveillance',
    description: 'Disease surveillance, alerts, investigations',
    requiredPermission: 'analytics',
    gradient: 'linear-gradient(135deg, #15803d 0%, #22c55e 100%)',
    accentColor: '#15803d',
    items: [
      { id: 'intelligence-home', label: 'Intelligence Home', path: '/ai/home' },
      { id: 'alert-program', label: 'Alert Program', path: '/ai/alerts' },
      { id: 'situation-reports', label: 'Situation Reports', path: '/ai/disease' },
      { id: 'hotspot-map', label: 'Hotspot Map', path: '/ai/hotspots' },
      { id: 'outbreak-response', label: 'Outbreak Response', path: '/ai/investigations' },
      { id: 'signals', label: 'Signal Dashboard', path: '/ai/signals' },
      { id: 'weekly-bulletin', label: 'Weekly Bulletin', path: '/ai/bulletin' },
      // Both routes gate on /data-studio rather than their own path, so the menu
      // filter has to check the same permission or the links would be hidden
      // from users who can in fact open the pages.
      { id: 'disease-status', label: 'Disease Status (Public)', path: '/disease-status', permission: '/data-studio' },
      { id: 'infodemic-review', label: 'Publication Review', path: '/infodemic-review', permission: '/data-studio' },
    ],
  },
  /* ── ANALYSIS — §9.7 Revised Sidebar ── */
  {
    id: 'analysis',
    label: 'Analysis',
    description: 'Deep analysis: lab, climate, one health, mortality',
    requiredPermission: 'analytics',
    gradient: 'linear-gradient(135deg, #2563eb 0%, #60a5fa 100%)',
    accentColor: '#2563eb',
    items: [
      { id: 'lab-analytics', label: 'Lab Analytics', path: '/ai/lab' },
      { id: 'ili-sari', label: 'ILI/SARI Sentinel', path: '/ai/ili-sari' },
      { id: 'weather-analytics', label: 'Weather Analytics', path: '/ai/weather' },
      { id: 'climate-disease', label: 'Climate & Disease', path: '/ai/climate' },
      { id: 'one-health', label: 'One Health', path: '/ai/one-health' },
      { id: 'animal-health', label: 'Animal Health', path: '/ai/animal-health' },
      { id: 'malaria-ira', label: 'Malaria Intelligence', path: '/ai/malaria' },
      { id: 'epi-coverage', label: 'Immunisation (EPI)', path: '/ai/epi' },
      { id: 'excess-mortality', label: 'Excess Mortality', path: '/ai/mortality' },
    ],
  },
  /* ── ANTIMICROBIAL RESISTANCE — OHASP AMR and hospital antibiotic use ── */
  {
    id: 'amr',
    label: 'Antimicrobial Resistance',
    description: 'Resistance, antibiograms, outbreak treatment risk, antibiotic use',
    requiredPermission: 'analytics',
    gradient: 'linear-gradient(135deg, #5b21b6 0%, #7e22ce 100%)',
    accentColor: '#5b21b6',
    // Every AMR page is guarded by the one /ai/amr page permission, so granting
    // the section is a single entry in a role's pages list.
    items: [
      { id: 'amr-overview', label: 'Overview', path: '/ai/amr' },
      { id: 'amr-scorecard', label: 'National Scorecard', path: '/ai/amr/scorecard', permission: '/ai/amr' },
      { id: 'amr-empirical-therapy', label: 'Empirical Therapy', path: '/ai/amr/empirical-therapy', permission: '/ai/amr' },
      { id: 'amr-antibiogram', label: 'Antibiogram', path: '/ai/amr/antibiogram', permission: '/ai/amr' },
      { id: 'amr-trends', label: 'Resistance Trends', path: '/ai/amr/trends', permission: '/ai/amr' },
      { id: 'amr-alerts', label: 'Alerts and Held Results', path: '/ai/amr/alerts', permission: '/ai/amr' },
      { id: 'amr-briefings', label: 'SitRep and Bulletin Briefings', path: '/ai/amr/briefings', permission: '/ai/amr' },
      { id: 'amr-outbreak-risk', label: 'Outbreak Treatment Risk', path: '/ai/amr/outbreak-risk', permission: '/ai/amr' },
      { id: 'amr-drug-resistant-tb', label: 'Drug-Resistant TB', path: '/ai/amr/drug-resistant-tb', permission: '/ai/amr' },
      { id: 'amr-one-health', label: 'One Health', path: '/ai/amr/one-health', permission: '/ai/amr' },
      { id: 'amr-climate', label: 'Climate and Resistance', path: '/ai/amr/climate', permission: '/ai/amr' },
      { id: 'amr-antibiotic-use', label: 'Hospital Antibiotic Use', path: '/ai/amr/antibiotic-use', permission: '/ai/amr' },
      { id: 'amr-coverage', label: 'Surveillance Coverage', path: '/ai/amr/coverage', permission: '/ai/amr' },
      { id: 'amr-literature', label: 'Literature', path: '/ai/amr/literature', permission: '/ai/amr' },
      { id: 'amr-data-quality', label: 'Data Quality', path: '/ai/amr/data-quality', permission: '/ai/amr' },
    ],
  },
  /* ── ENGINE — §9.7 Revised Sidebar ── */
  {
    id: 'engine',
    label: 'Analytics Engine',
    description: 'Algorithm catalog, monitoring, configuration',
    requiredPermission: 'analytics',
    gradient: 'linear-gradient(135deg, #6d28d9 0%, #8b5cf6 100%)',
    accentColor: '#6d28d9',
    items: [
      { id: 'algorithm-catalog', label: 'Algorithm Catalog', path: '/ai/catalog' },
      { id: 'agent-monitor', label: 'Agent Monitor', path: '/ai/agent-monitor' },
      { id: 'engine-monitoring', label: 'Performance', path: '/ai/performance' },
      { id: 'engine-overview', label: 'Transparency', path: '/ai/transparency' },
      { id: 'threshold-config', label: 'Thresholds & Alerts', path: '/ai/alert-config' },
      // Restored: dropped in the menu-config unification (22ef06a83) while the
      // page, its route and its permissions all survived — it was reachable
      // only by direct URL since then.
      { id: 'ai-knowledge', label: 'AI Knowledge & Training', path: '/ai/knowledge' },
    ],
  },
  /* ── DATA TOOLS — §9.7 Revised Sidebar ── */
  {
    id: 'data_tools',
    label: 'Data Tools',
    description: 'SQL explorer, visualizers, data exploration',
    requiredPermission: 'analytics',
    gradient: 'linear-gradient(135deg, #ef7d00 0%, #f59e0b 100%)',
    accentColor: '#ef7d00',
    items: [
      { id: 'data-studio', label: 'Data Studio', path: '/data-studio' },
      { id: 'sql-explorer', label: 'SQL Explorer', path: '/sql-explorer' },
      { id: 'data-visualizer', label: 'Aggregate Visualizer', path: '/ai/data-visualizer' },
      { id: 'event-visualizer', label: 'Case Visualizer', path: '/ai/event-visualizer' },
      { id: 'outlier-management', label: 'Outlier Management', path: '/dashboards/outliers' },
    ],
  },
  {
    id: 'administration',
    label: 'Administration',
    description: 'Users, logs, backups',
    requiredPermission: 'administration',
    gradient: 'linear-gradient(135deg, #475569 0%, #64748b 100%)',
    accentColor: '#475569',
    items: [
      { id: 'config', label: 'Configuration', path: '/config' },
      { id: 'system-config', label: 'System Settings', path: '/system-config' },
      { id: 'alerts', label: 'Alert Rules', path: '/alerts' },
      { id: 'email', label: 'Email Management', path: '/emails' },
      { id: 'disa-config', label: 'DISA Extractor', path: '/disa-config' },
      { id: 'security', label: 'Security & Access', path: '/security' },
      { id: 'credentials', label: 'Token Management', path: '/credentials' },
      { id: 'audit', label: 'Audit Log', path: '/audit' },
      { id: 'backup', label: 'Backup & Recovery', path: '/backup' },
    ],
  },
  {
    id: 'my_uploads',
    label: 'Data Upload',
    description: 'File upload portal',
    requiredPermission: 'my_uploads',
    gradient: 'linear-gradient(135deg, #d97706 0%, #f59e0b 100%)',
    accentColor: '#d97706',
    items: [
      { id: 'upload_portal', label: 'Upload Portal', path: '/uploads' },
    ],
  },
  {
    id: 'documentation',
    label: 'Documentation',
    description: 'Help and guides',
    requiredPermission: 'overview',  // Available to all logged-in users
    gradient: 'linear-gradient(135deg, #6366f1 0%, #818cf8 100%)',
    accentColor: '#6366f1',
    items: [
      { id: 'technical-docs', label: 'Technical Docs', path: '/docs' },
      { id: 'api-reference', label: 'API Reference', path: '/docs/api-reference' },
      { id: 'help', label: 'Help & Support', path: '/help' },
    ],
  },
];

// All feature permissions
export const FEATURE_PERMISSIONS: FeatureConfig[] = [
  { key: 'data_management', label: 'Data Management', description: 'Create/edit data sources and configurations', defaultValue: false },
  { key: 'pipeline_control', label: 'Pipeline Control', description: 'Trigger and manage data pipelines', defaultValue: false },
  { key: 'user_management', label: 'User Management', description: 'Manage users and permissions', defaultValue: false },
  { key: 'sql_publisher', label: 'SQL Publisher', description: 'Create views and publish SQL Explorer views as API endpoints', defaultValue: false },
  { key: 'ai_analytics', label: 'AI Analytics', description: 'Access AI-powered analytics features', defaultValue: true },
];

// Helper: Get all pages from menu sections
export const getAllPages = (): { path: string; label: string; section: string; sectionLabel: string }[] => {
  const pages: { path: string; label: string; section: string; sectionLabel: string }[] = [];
  
  for (const section of MENU_SECTIONS) {
    for (const item of section.items) {
      if (item.path) {
        pages.push({
          path: item.path,
          label: item.label,
          section: section.id,
          sectionLabel: section.label,
        });
      }
    }
  }
  
  return pages;
};

// Return type for default permissions
export interface DefaultPermissions {
  menu_sections: string[];
  pages: string[];
  features: {
    data_management: boolean;
    pipeline_control: boolean;
    user_management: boolean;
    sql_publisher: boolean;
    ai_analytics: boolean;
  };
}

// Helper: Get default permissions for a role type
export const getDefaultPermissions = (roleType: 'super_user' | 'admin' | 'editor' | 'viewer' | 'uploader'): DefaultPermissions => {
  const allSections = MENU_SECTIONS.map(s => s.id);
  const allPages = getAllPages().map(p => p.path);
  
  switch (roleType) {
    case 'super_user':
      return {
        menu_sections: allSections,
        pages: ['*'],  // Wildcard for all pages - full system access
        features: {
          data_management: true,
          pipeline_control: true,
          user_management: true,
          sql_publisher: true,
          ai_analytics: true,
        },
      };
    case 'admin':
      return {
        menu_sections: allSections,
        pages: ['*'],  // Wildcard for all pages
        features: {
          data_management: true,
          pipeline_control: true,
          user_management: true,
          sql_publisher: true,
          ai_analytics: true,
        },
      };
    case 'editor':
      return {
        menu_sections: ['overview', 'data_management', 'pipeline', 'monitoring', 'api_platform',
          'surveillance', 'analysis', 'amr', 'engine', 'data_tools', 'documentation'],
        pages: allPages.filter(p => 
          !p.startsWith('/security') && 
          !p.startsWith('/backup') &&
          !p.startsWith('/audit') &&
          !p.startsWith('/infrastructure') &&
          !p.startsWith('/config') &&
          !p.startsWith('/disa-config') &&
          !p.startsWith('/system-config') &&
          !p.startsWith('/emails') &&
          !p.startsWith('/credentials') &&
          !p.startsWith('/ai/settings')
        ),
        features: {
          data_management: true,
          pipeline_control: true,
          user_management: false,
          sql_publisher: false,
          ai_analytics: true,
        },
      };
    case 'viewer':
      return {
        menu_sections: ['overview', 'monitoring', 'surveillance', 'analysis', 'amr', 'data_tools', 'documentation'],
        pages: ['/',
          // Monitoring
          '/dashboards/pools', '/dashboards/system-health', '/dashboards/errors', '/dashboards/logs',
          // Surveillance (read-only)
          '/ai/home', '/ai/alerts', '/ai/disease', '/ai/hotspots', '/ai/signals', '/ai/bulletin',
          // Analysis (read-only)
          '/ai/lab', '/ai/weather', '/ai/climate', '/ai/one-health', '/ai/malaria', '/ai/mortality',
          '/ai/epi',
          // Antimicrobial Resistance (one permission guards every AMR page)
          '/ai/amr',
          '/ai/chat', '/ai/anomalies', '/ai/trends', '/ai/predictions',
          '/ai/dashboard', '/ai/reports', '/ai/explorer', '/ai/report-builder',
          // Data Tools
          '/sql-explorer', '/data-studio', '/ai/data-visualizer', '/ai/event-visualizer',
          '/dashboards/outliers',
          // Docs
          '/docs', '/docs/api-reference', '/help'],
        features: {
          data_management: false,
          pipeline_control: false,
          user_management: false,
          sql_publisher: false,
          ai_analytics: true,
        },
      };
    case 'uploader':
      return {
        menu_sections: ['my_uploads', 'documentation'],
        pages: ['/uploads', '/docs', '/help'],
        features: {
          data_management: false,
          pipeline_control: false,
          user_management: false,
          sql_publisher: false,
          ai_analytics: false,
        },
      };
    default:
      return getDefaultPermissions('viewer');
  }
};

// Check if user has access to a specific page
export const hasPageAccess = (pagePath: string, allowedPages: string[]): boolean => {
  if (!pagePath) return true;
  if (allowedPages.includes('*')) return true;  // Admin has all access
  
  // Check exact match first
  if (allowedPages.includes(pagePath)) return true;
  
  // Check path without hash (e.g., /docs#overview -> /docs)
  const pathWithoutHash = pagePath.split('#')[0];
  if (pathWithoutHash !== pagePath && allowedPages.includes(pathWithoutHash)) return true;
  
  // Check if any allowed page is a hash variant of this path
  // e.g., pagePath="/docs" matches allowed "/docs#overview"
  if (allowedPages.some(p => p.includes('#') && p.split('#')[0] === pagePath)) return true;
  
  return false;
};
